import type { ImportedMeshTopologyBinding } from './ImportedMeshTopologyBinding'
import { resolveImportedPickHostObjectId } from '../geometry'
import {
  resolveImportedBodyDefinitionId,
  resolveImportedBodyOccurrenceNodeId,
  resolveImportedTopologyBodyFamilyKey,
} from '../utils'

/**
 * Repository 独占的 Body occurrence 拓扑所属关系索引。
 *
 * 稳定 Body/occurrence 在注册时解析；几何身份在所属分区内校验，兼容 SDK 资产在 hydration
 * 后补写 geometryObjectId。查询不访问其他零件，不反复解码整个仓库，也不拥有任何场景资源。
 */
export class ImportedTopologyOwnerIndex {
  /** 每条已注册绑定所属的稳定 Body occurrence 分区。 */
  private readonly partitionKeyByRecord = new Map<ImportedMeshTopologyBinding, string>()

  /** 同一 Body occurrence 内的材质绑定及最近发布的几何身份。 */
  private readonly geometryIdsByPartition = new Map<string, Map<ImportedMeshTopologyBinding, string>>()

  /** 按几何身份进一步隔离的只读成员快照；无变化时保持同一数组引用。 */
  private readonly ownerRecordsByPartition = new Map<string, Map<string, readonly ImportedMeshTopologyBinding[]>>()

  /** 统一的空视图，不在缺失命中时分配临时数组。 */
  private readonly emptyRecords: readonly ImportedMeshTopologyBinding[] = Object.freeze([])

  /**
   * 注册绑定或在 occurrence 重绑定后重新定位它。
   * @param record 已通过 Repository 契约校验的绑定。
   */
  public register(record: ImportedMeshTopologyBinding): void {
    this.remove(record)
    const partitionKey = this.resolvePartitionKey(record)
    const geometryIds = this.geometryIdsByPartition.get(partitionKey)
      ?? new Map<ImportedMeshTopologyBinding, string>()
    geometryIds.set(record, resolveImportedPickHostObjectId(record.topologyAsset))
    this.partitionKeyByRecord.set(record, partitionKey)
    this.geometryIdsByPartition.set(partitionKey, geometryIds)
    this.publishPartition(partitionKey, geometryIds)
  }

  /**
   * 移除迁出或被释放的绑定；不释放共享拓扑宿主。
   * @param record 即将退出此 Repository 的绑定。
   */
  public remove(record: ImportedMeshTopologyBinding): void {
    const partitionKey = this.partitionKeyByRecord.get(record)
    if (partitionKey === undefined) return
    const geometryIds = this.geometryIdsByPartition.get(partitionKey)!
    geometryIds.delete(record)
    this.partitionKeyByRecord.delete(record)
    if (geometryIds.size === 0) {
      this.geometryIdsByPartition.delete(partitionKey)
      this.ownerRecordsByPartition.delete(partitionKey)
    } else {
      this.publishPartition(partitionKey, geometryIds)
    }
  }

  /**
   * 返回同一几何、Body 定义和 occurrence 的稳定只读成员视图。
   *
   * 资产 DTO 的几何 ID 可以后补；只检查当前 Body occurrence 的材质成员，发生变化后一次
   * 发布整个分区，防止仅更新目标记录导致另一个材质仍停留在旧组或漏掉新组的成员。
   * @param record 正在拾取或投影的绑定。
   * @returns 按注册顺序排列的同宿主记录。
   */
  public resolve(record: ImportedMeshTopologyBinding): readonly ImportedMeshTopologyBinding[] {
    const partitionKey = this.partitionKeyByRecord.get(record) ?? this.resolvePartitionKey(record)
    const geometryIds = this.geometryIdsByPartition.get(partitionKey)
    if (!geometryIds) return this.emptyRecords
    let changed = false
    for (const [member, previousGeometryId] of geometryIds) {
      const geometryId = resolveImportedPickHostObjectId(member.topologyAsset)
      if (geometryId !== previousGeometryId) {
        geometryIds.set(member, geometryId)
        changed = true
      }
    }
    if (changed) this.publishPartition(partitionKey, geometryIds)
    const geometryId = geometryIds.get(record) ?? resolveImportedPickHostObjectId(record.topologyAsset)
    return this.ownerRecordsByPartition.get(partitionKey)?.get(geometryId) ?? this.emptyRecords
  }

  /**
   * 清空当前文档的派生索引，解除对绑定及其 Mesh 的引用。
   */
  public clear(): void {
    this.partitionKeyByRecord.clear()
    this.geometryIdsByPartition.clear()
    this.ownerRecordsByPartition.clear()
  }

  /**
   * 解析与几何 hydration 无关的稳定分区身份。
   * @param record 当前 Mesh 的 Body 绑定。
   * @returns 保留完整 occurrence、不使用名称或 GPU 槽号的分区键。
   */
  private resolvePartitionKey(record: ImportedMeshTopologyBinding): string {
    const definitionId = resolveImportedBodyDefinitionId(record.bodyBindingId) || record.bodyBindingId
    const bodyIdentity = resolveImportedTopologyBodyFamilyKey(definitionId) || definitionId
    return JSON.stringify([bodyIdentity, resolveImportedBodyOccurrenceNodeId(record.bodyBindingId)])
  }

  /**
   * 在成员或几何身份改变时发布新的 owner 视图。
   * @param partitionKey 发生变化的 Body occurrence。
   * @param geometryIds 当前已校验的成员与几何身份。
   */
  private publishPartition(
    partitionKey: string,
    geometryIds: ReadonlyMap<ImportedMeshTopologyBinding, string>,
  ): void {
    const previousGroups = this.ownerRecordsByPartition.get(partitionKey)
    const recordsByGeometryId = new Map<string, ImportedMeshTopologyBinding[]>()
    geometryIds.forEach((geometryId, record) => {
      const records = recordsByGeometryId.get(geometryId) ?? []
      records.push(record)
      recordsByGeometryId.set(geometryId, records)
    })
    const nextGroups = new Map<string, readonly ImportedMeshTopologyBinding[]>()
    recordsByGeometryId.forEach((records, geometryId) => {
      const previousRecords = previousGroups?.get(geometryId)
      // 未变化的几何组保留版本引用，不能因兄弟组 hydration 就重建它的拓扑视觉资源。
      if (
        previousRecords
        && previousRecords.length === records.length
        && records.every((record, index) => record === previousRecords[index])
      ) {
        nextGroups.set(geometryId, previousRecords)
      } else {
        nextGroups.set(geometryId, Object.freeze(records))
      }
    })
    this.ownerRecordsByPartition.set(partitionKey, nextGroups)
  }
}
